import { EMMY_NOMINEE_IDS, EMMY_WINNER_ORDER } from "./tvCuratorPicks";
import NYT_BEST_TV from "./data/nytBestTvShows.json";
/** TV home shelf order — shared by AppleTvTvHome, sidebar nav, and list pages. */

import {
  GOLDEN_GLOBE_TV_CATEGORIES,
  getGoldenGlobeCategoryTmdbIds,
  isGoldenGlobeCategoryShelfKey,
} from "./appleTvGoldenGlobeShelf";
import {
  isEmmyCategoryShelfKey,
} from "./appleTvEmmyShelf";

const EMMY_WATCHLIST_SHELF = {
  key: "tv-emmy-2026",
  label: "Award Nominated",
  ranked: false,
  featured: true,
  shelfLimit: 200,
  heroMetricLabel: "Emmy Nominee",
  request: `/api/3/discover/tv?with_tmdb_ids=${EMMY_NOMINEE_IDS.join(",")}&sort_by=curator_score.desc`,
  subtitle: "2026 Emmy-nominated series · Curator score",
};

export const TV_COLLECTION_SHELF_KEYS = ["tv-emmy-winners-2026", "tv-nyt-best-century"];
const TV_COLLECTION_SHELVES = [
  {
    key: "tv-emmy-winners-2026", label: "2026 Emmy Winners", ranked: false, hiddenOnHome: true,
    heroMetricLabel: "2026 Emmy Winner", shelfLimit: 100,
    subtitle: "Drama · Comedy · Limited · Reality · Animated · Variety",
    request: `/api/3/discover/tv?with_tmdb_ids=${EMMY_WINNER_ORDER.join(",")}&sort_by=latest_season_date.desc`,
  },
  {
    key: "tv-nyt-best-century", label: "Curator Picks", ranked: false,
    heroMetricLabel: "Curator Pick", shelfLimit: 100,
    subtitle: "The New York Times 100 · newest selected season first",
    request: `/api/3/discover/tv?with_tmdb_ids=${NYT_BEST_TV.entries.map(entry => entry.tmdbId).join(",")}&sort_by=nyt_season_date.desc`,
  },
];

/** Large-card winner promos that precede each Golden Globe category shelf. */
export const TV_CATEGORY_WINNER_PROMOS = [
  {
    key: "promo-drama-winner",
    tmdbId: 250307, // The Pitt
    metricLabel: "Award-Winning Drama",
    shelfKey: "tv-gg-drama",
  },
  {
    key: "promo-comedy-winner",
    tmdbId: 124101, // Hacks
    metricLabel: "Award-Winning Comedy",
    shelfKey: "tv-gg-comedy",
  },
  {
    key: "promo-limited-winner",
    tmdbId: 206828, // DTF St. Louis
    metricLabel: "Award-Winning Limited Series",
    shelfKey: "tv-gg-limited",
  },
];

// Golden Globe rails (winner-first per year), relabeled to plain category names.
const GOLDEN_GLOBE_LABELS = { drama: "Drama", comedy: "Comedy", limited: "Limited Series" };
const GOLDEN_GLOBE_SHELF_DEFS = ["drama", "comedy", "limited"]
  .filter((key) => GOLDEN_GLOBE_TV_CATEGORIES.some((c) => c.key === key))
  .map((key) => ({
    key: `tv-gg-${key}`,
    label: GOLDEN_GLOBE_LABELS[key],
    ranked: false,
    shelfLimit: 200,
    loadType: "golden-globe-category",
    goldenGlobeCategory: key,
    heroMetricLabel: "Golden Globe Winner",
    request: `/api/3/discover/tv?with_tmdb_ids=${getGoldenGlobeCategoryTmdbIds(key).join(",")}&sort_by=curator_score.desc`,
    subtitle: "Winners and nominees by year",
  }));

export const TV_HOME_SHELF_DEFS = [
  {
    key: "tv",
    label: "Popular",
    ranked: true,
    heroMetricLabel: "Most Popular TV Show",
    request: "/api/3/discover/tv?list=popular&popular_source=imdb_meter&sort_by=imdb_meter.asc",
    subtitle: "IMDb TVmeter",
  },
  EMMY_WATCHLIST_SHELF,
  ...TV_COLLECTION_SHELVES,
  ...GOLDEN_GLOBE_SHELF_DEFS,
  {
    key: "tv-reality-tvstats",
    label: "Reality",
    ranked: true,
    hideScore: true,
    heroMetricLabel: "Most Popular Reality Show",
    request: "/api/3/discover/tv?telestats_list=reality_today",
    subtitle: "TelevisionStats · reality engagement",
  },
  {
    key: "tv-top-rated",
    hiddenOnHome: true,
    label: "All",
    ranked: true,
    heroMetricLabel: "Top Rated on Curator",
    request: "/api/3/discover/tv?list=top_rated&top_rated_source=curator&sort_by=curator_score.desc",
    subtitle: "Top curator score",
  },
];

export const TV_HOME_SHELF_NAV_ITEMS = [
  { key: "tv-home", label: "TV Home" },
  ...TV_HOME_SHELF_DEFS.filter(def => !def.hiddenOnHome).map(({ key, label }) => ({ key, label })),
];

/** Keys that preserve API rank order on list pages. */
export const TV_HOME_RANKED_LIST_KEYS = new Set(
  [...TV_HOME_SHELF_DEFS.filter((def) => def.ranked).map((def) => def.key), ...TV_COLLECTION_SHELF_KEYS, "tv-emmy-2026"],
);

/** Award category shelves (Emmy + Golden Globe): winner-first per-year ordering,
 *  preserved on list pages via rank-asc sort. */
export const TV_HOME_AWARD_LIST_KEYS = new Set(
  TV_HOME_SHELF_DEFS
    .filter((def) => isGoldenGlobeCategoryShelfKey(def.key) || isEmmyCategoryShelfKey(def.key))
    .map((def) => def.key),
);

/** Emmy consolidated category shelves. */
export const TV_HOME_EMMY_LIST_KEYS = new Set(
  TV_HOME_SHELF_DEFS.filter((def) => isEmmyCategoryShelfKey(def.key)).map((def) => def.key),
);

/** Golden Globe consolidated category shelves: winner-first per year ordering. */
export const TV_HOME_GOLDEN_GLOBE_LIST_KEYS = new Set(
  TV_HOME_SHELF_DEFS.filter((def) => isGoldenGlobeCategoryShelfKey(def.key)).map((def) => def.key),
);

export function buildTvHomeListConfig() {
  return Object.fromEntries(
    TV_HOME_SHELF_DEFS.map((def) => [
      def.key,
      {
        label: def.label,
        subtitle: def.subtitle,
        request: def.request,
        mediaType: "tv",
        loadType: def.loadType || null,
        goldenGlobeCategory: def.goldenGlobeCategory || null,
        emmyCategory: def.emmyCategory || null,
      },
    ]),
  );
}
